import PropTypes from 'prop-types';
import React, { useState, useContext, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import connect from '../utils/connect';
import './pacing.scss';
import {
    onTrack,
    offTrack,
    goalReached,
    expired,
    completed,
} from '../constants/pacingStatus';
import { getStatusLabel, getStatusContext } from '../utils/pacingStatus';
import Status from './Status';

import { isEmpty } from 'ramda';

import Card from '@app/ui/sports-components/Card';
import { Row, Col, Flex, Progress, theme, LoadingDots } from '@app/ui';
import classNames from '@app/ui/sports-components/utils/classNames';
import SearchSelect from '@app/ui/sports-components/SearchSelect';
import Tooltip from '@app/ui/sports-components/Tooltip';

import { PacingContext } from './Context';
import { percentage, shortDate, decimal } from '../utils/formatters';
import shortenStr from '../utils/shortenStr';
import shortenArr from '../utils/shortenArr';
import { metricOptions } from '../constants/pacingMetrics';

const Highlight = ({ pacing }) => {
    const { t } = useTranslation('tools');
    const { token } = theme.useToken();
    const { isLoading, goals } = pacing;
    const { currentGoalId, setCurrentGoalId, currentoGoalOptions } =
        useContext(PacingContext);
    const [selectedStatusId, setSelectedStatusId] = useState(null);

    useEffect(() => {
        if (!currentGoalId) return;
        if (goals.findIndex(g => g.id === currentGoalId) >= 0) return;
        setCurrentGoalId(null);
    }, [goals]);

    const currentGoal = useMemo(
        () => goals.find(g => g.id === currentGoalId) || {},
        [goals, currentGoalId],
    );

    const hasCurrentGoal = currentGoal && !isEmpty(currentGoal);
    const {
        metricUpdatedAt,
        rightsholderName,
        sponsorName,
        placementNames,
        statusId,
        targetValue,
        currentValue,
        metricId,
        startDate,
        endDate,
    } = currentGoal;

    const getCount = statusList =>
        goals.filter(({ statusId }) => statusList.includes(statusId)).length;

    const goalCount = getCount([
        onTrack,
        offTrack,
        goalReached,
        completed,
        expired,
    ]);

    const statusList = useMemo(
        () => [
            {
                status: goalReached,
                label: t('pacing.status.goalReached'),
                message: t('pacing.highlight.statusMessages.goalReached'),
                count: getCount([goalReached, completed]),
            },
            {
                status: onTrack,
                label: t('pacing.status.onTrack'),
                message: t('pacing.highlight.statusMessages.onTrack'),
                count: getCount([onTrack]),
            },
            {
                status: offTrack,
                label: t('pacing.status.offTrack'),
                message: t('pacing.highlight.statusMessages.offTrack'),
                count: getCount([offTrack, expired]),
            },
        ],
        [goals, t],
    );

    const selectedStatusItem = useMemo(
        () => statusList.find(s => s.status === selectedStatusId),
        [statusList, selectedStatusId],
    );

    const selectedLabel = selectedStatusItem?.label;
    const selectedMessage =
        selectedStatusItem?.message || t('pacing.highlight.selectGoalInfo');

    const progressValue = useMemo(() => {
        if (!targetValue) return 0;
        const value = Math.round(
            (parseInt(currentValue) / parseInt(targetValue)) * 100,
        );
        if (value > 100) return 100;
        return Math.round(
            (parseInt(currentValue) / parseInt(targetValue)) * 100,
        );
    }, [currentGoal]);

    const progressLabel = useMemo(() => {
        if (!targetValue) return null;
        return t('pacing.highlight.ofGoal', {
            percentage: percentage(
                parseInt(currentValue) / parseInt(targetValue),
                {
                    maximumFractionDigits: 0,
                },
            ),
        });
    }, [currentGoal, t]);

    const currentGoalMetric = useMemo(() => {
        if (!metricId) return null;
        const metricOption = metricOptions.find(mo => mo.value === metricId);
        if (!metricOption) return null;
        return metricOption.name.toLowerCase();
    }, [currentGoal]);

    const progressLabelColor = progressValue > 60 ? 'white' : '#7a7a7a';
    const progressContext = getStatusContext(statusId);
    const progressStrokeColor =
        progressContext === 'suc'
            ? token.colorSuccess
            : progressContext === 'dan'
              ? token.colorError
              : token.colorPrimary;

    const remainingDays = useMemo(() => {
        const value =
            parseInt(
                (new Date(endDate).getTime() - new Date().getTime()) /
                    (3600 * 1000 * 24),
            ) + 1;
        return value > 0 ? value : 0;
    }, [startDate, endDate]);

    const totalDays = useMemo(
        () =>
            parseInt(
                (new Date(endDate).getTime() - new Date(startDate).getTime()) /
                    (3600 * 1000 * 24),
            ) + 1,
        [startDate, endDate],
    );

    const showLoading = isLoading && goals.length === 0;
    return (
        <div>
            <Card>
                <Flex
                    vertical
                    style={{
                        height: '200px',
                    }}
                    className="gds-layout__container"
                >
                    {showLoading && (
                        <div className="-full-height">
                            <LoadingDots size="lg" />
                        </div>
                    )}
                    {!showLoading && (
                        <Row gutter={16} className="-full-height">
                            <Col md={8}>
                                <Flex
                                    vertical
                                    className="gds-layout__container -m-t-2"
                                >
                                    <Row gutter={16}>
                                        <p className="gds-form-group__label">
                                            {t('pacing.highlight.title')}
                                        </p>
                                    </Row>
                                    <Row gutter={16}>
                                        <Col md={12}>
                                            <div className="-goal-count-container -m-t-3 ">
                                                <div>
                                                    <p className="gds-text--header-md gds-text--bold">
                                                        {goalCount}
                                                    </p>
                                                </div>
                                                <p className="gds-text--body-sm">
                                                    {t(
                                                        'pacing.highlight.goalsSet',
                                                    )}
                                                </p>
                                            </div>
                                        </Col>
                                        <Col md={12}>
                                            <div>
                                                {statusList.map(
                                                    ({
                                                        status,
                                                        label,
                                                        count,
                                                    }) => {
                                                        const onClick = () =>
                                                            setSelectedStatusId(
                                                                status,
                                                            );
                                                        const isSelected =
                                                            status ===
                                                            selectedStatusId;
                                                        const containerClasses =
                                                            classNames({
                                                                '-color-bg-lt-2':
                                                                    isSelected,
                                                            });
                                                        return (
                                                            <div
                                                                key={status}
                                                                onClick={
                                                                    onClick
                                                                }
                                                                className={
                                                                    containerClasses
                                                                }
                                                            >
                                                                <span className="-m-l-2 -status-count -m-r-2">
                                                                    {count}
                                                                </span>
                                                                <Status
                                                                    statusId={
                                                                        status
                                                                    }
                                                                    hideValues
                                                                />
                                                                {label}
                                                            </div>
                                                        );
                                                    },
                                                )}
                                            </div>
                                        </Col>
                                    </Row>
                                    <Row gutter={16} className="-m-t-2">
                                        <p className="gds-text--body-sm -status-message-container">
                                            {selectedLabel && (
                                                <span className="gds-text--bold">
                                                    {selectedLabel}:{' '}
                                                </span>
                                            )}
                                            {selectedMessage}
                                        </p>
                                    </Row>
                                </Flex>
                            </Col>
                            <div className="-pacing-highlight-divider" />
                            <Col md={16}>
                                <Flex
                                    vertical
                                    className="gds-layout__container -m-t-2"
                                >
                                    <Row gutter={16}>
                                        <Col md={24}>
                                            <div>
                                                <span className="gds-form-group__label team-highlight-title">
                                                    {t(
                                                        'pacing.highlight.selectedGoal',
                                                    )}
                                                </span>
                                                <span className="regular-font-size">
                                                    {' '}
                                                    -
                                                </span>
                                                <span className="team-selector">
                                                    <SearchSelect
                                                        name="teamSelector"
                                                        size="sm"
                                                        placeholder={t(
                                                            'pacing.highlight.selectGoal',
                                                        )}
                                                        options={
                                                            currentoGoalOptions
                                                        }
                                                        value={currentGoalId}
                                                        onChange={
                                                            setCurrentGoalId
                                                        }
                                                        hideClearButton
                                                    />
                                                </span>
                                                {hasCurrentGoal && (
                                                    <>
                                                        <span className="gds-text--body-sm gds-text--italic -m-l-3">
                                                            {t(
                                                                'pacing.highlight.lastUpdated',
                                                                {
                                                                    date: metricUpdatedAt
                                                                        ? shortDate(
                                                                              metricUpdatedAt,
                                                                          )
                                                                        : t(
                                                                              'pacing.highlight.never',
                                                                          ),
                                                                },
                                                            )}
                                                        </span>
                                                        <Tooltip
                                                            text={t(
                                                                'pacing.highlight.tooltip',
                                                            )}
                                                            position="left"
                                                        >
                                                            <i className="fas fa-info-circle -p-l-2" />
                                                        </Tooltip>
                                                    </>
                                                )}
                                            </div>
                                        </Col>
                                    </Row>
                                    {hasCurrentGoal && (
                                        <Row
                                            gutter={16}
                                            style={{
                                                height: '120px',
                                            }}
                                        >
                                            <Col
                                                md={6}
                                                className="-full-height"
                                            >
                                                <div className="-vertical-center">
                                                    <p className="gds-text--body-md gds-text--bold">
                                                        {shortenStr(
                                                            20,
                                                            rightsholderName,
                                                        )}
                                                    </p>
                                                    <p className="gds-text--body-md gds-text--bold">
                                                        {shortenStr(
                                                            20,
                                                            sponsorName,
                                                        )}
                                                    </p>
                                                    {!isEmpty(
                                                        placementNames,
                                                    ) && (
                                                        <div
                                                            className="gds-tooltip gds-tooltip--default gds-tooltip--left -inline-block"
                                                            data-tooltip={`${placementNames.join(
                                                                '\n',
                                                            )}`}
                                                        >
                                                            <p className="gds-text--body-sm">
                                                                {shortenArr(
                                                                    placementNames,
                                                                    'Placements',
                                                                )}
                                                                <i className="fas fa-info-circle -p-l-2" />
                                                            </p>
                                                        </div>
                                                    )}

                                                    <div>
                                                        <Status
                                                            statusId={statusId}
                                                            hideValues
                                                        />
                                                        {getStatusLabel(
                                                            statusId,
                                                        )}
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col
                                                md={12}
                                                className="-full-height"
                                            >
                                                <div className="-vertical-center -progress-bar-container">
                                                    <Progress
                                                        percent={progressValue}
                                                        format={() => (
                                                            <span
                                                                style={{
                                                                    color: progressLabelColor,
                                                                }}
                                                            >
                                                                {progressLabel}
                                                            </span>
                                                        )}
                                                        percentPosition={{
                                                            align: 'center',
                                                            type: 'inner',
                                                        }}
                                                        strokeColor={
                                                            progressStrokeColor
                                                        }
                                                    />
                                                    {currentGoalMetric &&
                                                        targetValue && (
                                                            <p className="gds-text--body-sm -text-align-center">
                                                                <span className="gds-text--bold">
                                                                    {decimal(
                                                                        parseInt(
                                                                            currentValue,
                                                                        ),
                                                                    )}
                                                                </span>
                                                                {` / ${decimal(
                                                                    parseInt(
                                                                        targetValue,
                                                                    ),
                                                                )} ${currentGoalMetric}`}
                                                            </p>
                                                        )}
                                                </div>
                                            </Col>
                                            <Col
                                                md={6}
                                                className="-full-height"
                                            >
                                                <div className="-vertical-center -text-align-end">
                                                    <p className="gds-text--body-sm gds-text--bold">
                                                        {shortDate(startDate)} -{' '}
                                                        {shortDate(endDate)}
                                                    </p>
                                                    <p className="gds-text--body-md">
                                                        <span className="gds-text--bold">
                                                            {remainingDays}
                                                        </span>{' '}
                                                        / {totalDays}
                                                    </p>
                                                    <p
                                                        className="gds-text--body-md"
                                                        style={{
                                                            lineHeight: 0.5,
                                                        }}
                                                    >
                                                        {t(
                                                            'pacing.highlight.daysRemaining',
                                                        )}
                                                    </p>
                                                </div>
                                            </Col>
                                        </Row>
                                    )}
                                    {!hasCurrentGoal && (
                                        <Row
                                            gutter={16}
                                            style={{
                                                height: '120px',
                                            }}
                                        >
                                            <Col
                                                md={24}
                                                className="-full-height"
                                            >
                                                <div className="-vertical-center">
                                                    <p className="gds-text--body-md -text-align-center">
                                                        {t(
                                                            'pacing.highlight.selectGoalProgress',
                                                        )}
                                                    </p>
                                                </div>
                                            </Col>
                                        </Row>
                                    )}
                                </Flex>
                            </Col>
                        </Row>
                    )}
                </Flex>
            </Card>
        </div>
    );
};

Highlight.propTypes = {
    pacing: PropTypes.object,
};

export default connect(['client', 'pacing'])(Highlight);
